/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on
 * an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useEffect, useState, useRef } from 'react';
import { styled, css, t, useTheme } from '@superset-ui/core';
import Icons from 'src/components/Icons';
import ControlHeader from 'src/explore/components/ControlHeader';
import { LinkFormattingPopover } from './LinkFormattingPopover';
import {
  LinkFormattingRule,
  LinkFormattingControlProps,
  LINK_TYPE,
} from './types';
import {
  AddControlLabel,
  CaretContainer,
  Label,
  OptionControlContainer,
} from '../OptionControls';

const FormattersContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.gridUnit}px;
    border: solid 1px ${theme.colors.grayscale.light2};
    border-radius: ${theme.gridUnit}px;
  `}
`;

export const FormatterContainer = styled(OptionControlContainer)`
  &,
  & > div {
    margin-bottom: ${({ theme }) => theme.gridUnit}px;
    :last-child {
      margin-bottom: 0;
    }
  }
`;

export const CloseButton = styled.button`
  ${({ theme }) => css`
    color: ${theme.colors.grayscale.light1};
    height: 100%;
    width: ${theme.gridUnit * 6}px;
    border: none;
    border-right: solid 1px ${theme.colors.grayscale.dark2}0C;
    padding: 0;
    outline: none;
    border-bottom-left-radius: 3px;
    border-top-left-radius: 3px;
  `}
`;

const LinkFormattingControl = ({
  value,
  onChange,
  columnOptions,
  verboseMap,
  removeIrrelevantConditions,
  ...props
}: LinkFormattingControlProps) => {
  const theme = useTheme();
  const [linkRules, setLinkRules] = useState<LinkFormattingRule[]>(value ?? []);
  // 用于跟踪是否是用户主动修改（非初始化）
  const isUserChange = useRef(false);
  // 用于跟踪是否已经完成初始化
  const isInitialized = useRef(false);

  // 同步外部 value 变化到内部 state
  useEffect(() => {
    if (value !== undefined && !isUserChange.current) {
      setLinkRules(value ?? []);
    }
    isUserChange.current = false;
  }, [value]);

  useEffect(() => {
    // 只在初始化完成后且是用户主动修改时才调用 onChange
    if (isInitialized.current && onChange) {
      isUserChange.current = true;
      onChange(linkRules);
    }
  }, [linkRules, onChange]);

  // 标记初始化完成
  useEffect(() => {
    isInitialized.current = true;
  }, []);

  useEffect(() => {
    // 只有当 columnOptions 有值时才进行清理，避免在数据加载过程中误删格式化规则
    // 同时确保 value 已经完全加载，避免在初始化阶段误删
    if (
      removeIrrelevantConditions &&
      columnOptions &&
      columnOptions.length > 0 &&
      value !== undefined
    ) {
      const newRules = linkRules.filter(config =>
        columnOptions.some((option: any) => option?.value === config?.column),
      );
      if (newRules.length !== linkRules.length) {
        setLinkRules(newRules);
      }
    }
  }, [linkRules, columnOptions, removeIrrelevantConditions, value]);

  const onDelete = (index: number) => {
    setLinkRules(prevRules => prevRules.filter((_, i) => i !== index));
  };

  const onSave = (config: LinkFormattingRule) => {
    setLinkRules(prevRules => [...prevRules, config]);
  };

  const onEdit = (newConfig: LinkFormattingRule, index: number) => {
    const newRules = [...linkRules];
    newRules.splice(index, 1, newConfig);
    setLinkRules(newRules);
  };

  const createLabel = (config: LinkFormattingRule) => {
    const columnName =
      (config.column && verboseMap?.[config.column]) ?? config.column;
    if (config.linkType === LINK_TYPE.IMAGE) {
      return `${columnName} - 图片 (${config.imageWidth}x${config.imageHeight})`;
    }
    return `${columnName} - 超链接 "${config.displayText}"`;
  };

  return (
    <div>
      <ControlHeader {...props} />
      <FormattersContainer>
        {linkRules.map((config, index) => (
          <FormatterContainer key={index}>
            <CloseButton onClick={() => onDelete(index)}>
              <Icons.XSmall iconColor={theme.colors.grayscale.light1} />
            </CloseButton>
            <LinkFormattingPopover
              title={t('编辑格式化器')}
              config={config}
              columns={columnOptions}
              onChange={(newConfig: LinkFormattingRule) =>
                onEdit(newConfig, index)
              }
              destroyTooltipOnHide
            >
              <OptionControlContainer withCaret>
                <Label>{createLabel(config)}</Label>
                <CaretContainer>
                  <Icons.CaretRight iconColor={theme.colors.grayscale.light1} />
                </CaretContainer>
              </OptionControlContainer>
            </LinkFormattingPopover>
          </FormatterContainer>
        ))}
        <LinkFormattingPopover
          title={t('添加新的格式化器')}
          columns={columnOptions}
          onChange={onSave}
          destroyTooltipOnHide
        >
          <AddControlLabel>
            <Icons.PlusSmall iconColor={theme.colors.grayscale.light1} />
            {t('添加超链接格式化')}
          </AddControlLabel>
        </LinkFormattingPopover>
      </FormattersContainer>
    </div>
  );
};

export default LinkFormattingControl;
